import { useEffect, useState } from "react";
import { api } from "../../../lib/api";
import { getAuthToken } from "../../../lib/auth";
import { toast } from "../../../lib/ui";

// Module copy of ChatLayout's pure key helper (useAndroidShare pattern).
const fq = (vId: string, cId: string) => (cId.includes(':') ? cId : `${vId}:${cId}`);

type UseFavoritesOptions = {
  token: string;
};

/**
 * Favorite-channel state (step 5.1d). `favorites` is the pinned id list — a mix
 * of fully-qualified channel ids ("space:general", "dm_abc:chat") and
 * kanban-list ids ("klist:<voidId>:<channelId>:<listId>") — backed by
 * localStorage and the canonical `/users/me/favorites` server copy. Owns the
 * membership/toggle helpers (`isFav`/`toggleFav`, `isListFav`/`toggleListFav`)
 * and the `parseListFav` decoder.
 *
 * Deliberately tight scope (3.2 useUnread pattern): this owns the list + its
 * persistence, NOT the things that react to it. The favorites-coupled effects
 * stay in ChatLayout and read the returned list — the kanban-preload effects
 * (call `loadKanbanIfNeeded`, 5.2 territory), the favorites+previews effect, and
 * the landing-widget autoscroll effect. Input is just `token` (server-load dep).
 */
export function useFavorites({ token }: UseFavoritesOptions) {
  const [favorites, setFavorites] = useState<string[]>(() => {
    try { const raw = localStorage.getItem('favorites'); return raw ? JSON.parse(raw) : []; } catch { return []; }
  });
  // Load favorites from server (canonical) on mount/token change
  useEffect(() => {
    (async () => {
      try {
        const tok = getAuthToken() || '';
        if (!tok) return;
        const res = await api.getAuth('/users/me/favorites', tok);
        const arr = Array.isArray(res?.favorites) ? res.favorites as string[] : [];
        setFavorites(arr);
        try { localStorage.setItem('favorites', JSON.stringify(arr)); } catch { /* noop */ }
      } catch { /* noop */ }
    })();
  }, [token]);
  useEffect(() => { try { localStorage.setItem('favorites', JSON.stringify(favorites)); } catch { /* noop */ } }, [favorites]);
  const isFav = (vId: string, cId: string) => favorites.includes(fq(vId, cId));
  const toggleFav = (vId: string, cId: string) => {
    const id = fq(vId, cId);
    const wasFav = favorites.includes(id);
    setFavorites(prev => { if (prev.includes(id)) return prev.filter(x => x !== id); if (prev.length >= 4) { toast('You can pin up to 4 favorites', 'error'); return prev; } return [...prev, id]; });
    (async () => { try { const tok = getAuthToken() || ''; if (!tok) return; if (wasFav) await api.deleteAuth('/users/me/favorites', { id }, tok); else await api.postAuth('/users/me/favorites', { id }, tok); } catch { /* noop */ } })();
  };
  // Kanban list favorites support (id format: klist:<voidId>:<channelId>:<listId>)
  const listFavId = (vId: string, cId: string, listId: string) => `klist:${vId}:${cId}:${listId}`;
  const isListFav = (vId: string, cId: string, listId: string) => favorites.includes(listFavId(vId, cId, listId));
  const toggleListFav = (vId: string, cId: string, listId: string) => {
    const id = listFavId(vId, cId, listId);
    const wasFav = favorites.includes(id);
    setFavorites(prev => { if (prev.includes(id)) return prev.filter(x => x !== id); if (prev.length >= 4) { toast('You can pin up to 4 favorites', 'error'); return prev; } return [...prev, id]; });
    (async () => { try { const tok = getAuthToken() || ''; if (!tok) return; if (wasFav) await api.deleteAuth('/users/me/favorites', { id }, tok); else await api.postAuth('/users/me/favorites', { id }, tok); } catch { /* noop */ } })();
  };
  function parseListFav(fid: string): { vId: string; cIdRaw: string; fqid: string; listId: string } | null {
    if (!fid.startsWith('klist:')) return null;
    const rest = fid.slice(6); // after 'klist:'
    const last = rest.lastIndexOf(':');
    if (last === -1) return null;
    const listId = rest.slice(last + 1);
    const vc = rest.slice(0, last);
    const first = vc.indexOf(':');
    if (first === -1) return null;
    const vId = vc.slice(0, first);
    const cIdRaw = vc.slice(first + 1); // may be short or fully-qualified
    const fqid = cIdRaw.includes(':') ? cIdRaw : `${vId}:${cIdRaw}`;
    return { vId, cIdRaw, fqid, listId };
  }

  return {
    favorites,
    setFavorites,
    isFav,
    toggleFav,
    isListFav,
    toggleListFav,
    parseListFav,
  };
}
